import { useCallback } from 'react';
import type {
  OntologyGraphTree,
  OntologyGraphLayer,
  OPMGraphData,
} from '../../../types/opm';
import {
  normalizeGraphTree,
  cloneGraphData,
  createGraphPayload,
} from '../utils/graphLayerHelpers';

interface UseOPMFileOperationsProps {
  setNodeCounter: (counter: number | ((prev: number) => number)) => void;
  setGlobalElementOrder: (order: number | ((prev: number) => number)) => void;
  setElementAddHistory: (history: any[] | ((prev: any[]) => any[])) => void;
  updateGraphData: () => void;
  handleResetView: () => void;
  getGraphTree: () => OntologyGraphTree;
  onTreeImport: (tree: OntologyGraphTree) => void;
}

const ensureTextPosition = (node: any) => {
  if (node.text) {
    if (typeof node.text === 'string') {
      node.text = {
        value: node.text,
        x: node.x,
        y: node.y,
      };
    } else if (typeof node.text === 'object' && node.text.value !== undefined) {
      if (typeof node.x === 'number') node.text.x = node.x;
      if (typeof node.y === 'number') node.text.y = node.y;
    }
  }
};

const extractMaxCounter = (nodes: any[]): number => {
  return nodes.reduce((max, node) => {
    const match = String(node.id || '').match(/_(\d+)$/);
    if (match) {
      return Math.max(max, parseInt(match[1], 10));
    }
    return max;
  }, 0);
};

export const useOPMFileOperations = ({
  setNodeCounter,
  setGlobalElementOrder,
  setElementAddHistory,
  updateGraphData,
  handleResetView,
  getGraphTree,
  onTreeImport,
}: UseOPMFileOperationsProps) => {
  const processNodeDefaults = useCallback(
    (node: any, index: number, allNodes: any[]) => {
      const next = { ...node };
      if (!next.id) next.id = `imported_node_${index}`;
      if (!next.type) next.type = 'rect';

      if (typeof next.x !== 'number' || typeof next.y !== 'number') {
        if (next.type !== 'state' || !next.properties?.parentGroupId) {
          const gridSize = 200;
          const nodesPerRow = 3;
          const row = Math.floor(index / nodesPerRow);
          const col = index % nodesPerRow;

          next.x = 300 + col * gridSize;
          next.y = 200 + row * gridSize;
        }
      }

      if (typeof next.width !== 'number' || typeof next.height !== 'number') {
        switch (next.type) {
          case 'rect':
            next.width = next.width || 180;
            next.height = next.height || 80;
            break;
          case 'circle':
            next.width = next.width || 100;
            next.height = next.height || 100;
            break;
          case 'diamond':
            next.width = next.width || 120;
            next.height = next.height || 120;
            break;
          case 'polygon':
            next.width = next.width || 150;
            next.height = next.height || 80;
            break;
          case 'state':
            next.width = next.width || 70;
            next.height = next.height || 30;
            break;
          case 'dynamic-group':
            next.width = next.width || 300;
            next.height = next.height || 200;
            break;
          default:
            next.width = next.width || 150;
            next.height = next.height || 100;
        }
      }

      if (next.text) {
        if (typeof next.text === 'string') {
          next.text = {
            value: next.text,
            x: next.x,
            y: next.y,
          };
        } else if (typeof next.text === 'object') {
          if (next.text.value === undefined) {
            next.text.value = next.properties?.name || '';
          }
          if (typeof next.x === 'number' && next.text.x === undefined) {
            next.text.x = next.x;
          }
          if (typeof next.y === 'number' && next.text.y === undefined) {
            next.text.y = next.y;
          }
        }
      } else if (next.properties?.name) {
        next.text = {
          value: next.properties.name,
          x: next.x,
          y: next.y,
        };
      } else {
        next.text = {
          value: '',
          x: next.x,
          y: next.y,
        };
      }

      if (next.type === 'dynamic-group') {
        next.properties = {
          name: next.properties?.name || '',
          description: next.properties?.description || '',
          width: next.width,
          height: next.height,
          isGroup: true,
          children: next.children || next.properties?.children || [],
          nodeType: 'rect',
          autoResize: false,
          isRestrict: true,
          collapsible: false,
          isCollapsed: false,
          originalType: 'dynamic-group',
          isEntityGroup: true,
          ...next.properties,
        };
      }

      if (next.type === 'state') {
        next.properties = {
          name: next.properties?.name || '',
          description: next.properties?.description || '',
          width: next.width,
          height: next.height,
          isState: true,
          nodeType: 'state',
          parentGroupId: next.properties?.parentGroupId || '',
          ...next.properties,
        };
      }

      if (next.type === 'value') {
        next.properties = {
          name: next.properties?.name || '',
          description: next.properties?.description || '',
          width: next.width,
          height: next.height,
          isValue: true,
          nodeType: 'value',
          parentGroupId: next.properties?.parentGroupId || '',
          ...next.properties,
        };
      }

      return next;
    },
    []
  );

  const processStateNodePosition = useCallback((node: any, allNodes: any[]) => {
    const next = { ...node };
    if (next.type === 'state' && next.properties?.parentGroupId) {
      // 如果节点已经有有效坐标，不重新计算位置（保留导入数据的原始位置）
      if (typeof next.x === 'number' && typeof next.y === 'number') {
        return next;
      }
      const parentNode = allNodes.find(
        item => item.id === next.properties.parentGroupId
      );
      if (parentNode) {
        const siblings = allNodes.filter(
          item =>
            item.properties?.parentGroupId === next.properties.parentGroupId &&
            item.type === 'state'
        );
        const stateIndex = siblings.findIndex(item => item.id === next.id);
        const padding = 30;
        const stateSpacing = 10;
        const stateWidth = next.width || 70;
        const stateHeight = next.height || 30;
        const parentLeft = parentNode.x - parentNode.width / 2 + padding;
        const parentTop = parentNode.y - parentNode.height / 2 + padding + 30;
        const parentRight = parentNode.x + parentNode.width / 2 - padding;
        const availableWidth = parentRight - parentLeft;
        const statesPerRow = Math.max(
          1,
          Math.floor(availableWidth / (stateWidth + stateSpacing))
        );
        const row = Math.floor(stateIndex / statesPerRow);
        const col = stateIndex % statesPerRow;
        next.x =
          parentLeft + col * (stateWidth + stateSpacing) + stateWidth / 2;
        next.y =
          parentTop + row * (stateHeight + stateSpacing) + stateHeight / 2;
      }
    }
    return next;
  }, []);

  const processValueNodePosition = useCallback((node: any, allNodes: any[]) => {
    const next = { ...node };
    if (next.type === 'value' && next.properties?.parentGroupId) {
      // 如果节点已经有有效坐标，不重新计算位置（保留导入数据的原始位置）
      if (typeof next.x === 'number' && typeof next.y === 'number') {
        return next;
      }
      const parentNode = allNodes.find(
        item => item.id === next.properties.parentGroupId
      );
      if (parentNode) {
        const siblings = allNodes.filter(
          item =>
            item.properties?.parentGroupId === next.properties.parentGroupId &&
            item.type === 'value'
        );
        const valueIndex = siblings.findIndex(item => item.id === next.id);
        const padding = 30;
        const valueSpacing = 10;
        const valueWidth = next.width || 70;
        const valueHeight = next.height || 30;
        const parentLeft = parentNode.x - parentNode.width / 2 + padding;
        const parentTop = parentNode.y - parentNode.height / 2 + padding + 30;
        const parentRight = parentNode.x + parentNode.width / 2 - padding;
        const availableWidth = parentRight - parentLeft;
        const valuesPerRow = Math.max(
          1,
          Math.floor(availableWidth / (valueWidth + valueSpacing))
        );
        const row = Math.floor(valueIndex / valuesPerRow);
        const col = valueIndex % valuesPerRow;
        next.x =
          parentLeft + col * (valueWidth + valueSpacing) + valueWidth / 2;
        next.y =
          parentTop + row * (valueHeight + valueSpacing) + valueHeight / 2;
      }
    }
    return next;
  }, []);

  const processGroupNodeSize = useCallback((node: any, allNodes: any[]) => {
    const next = { ...node };
    if (next.type === 'dynamic-group' && next.children) {
      // 如果节点已经有有效坐标和尺寸，不重新计算（保留导入数据的原始位置和大小）
      const hasValidPosition =
        typeof next.x === 'number' && typeof next.y === 'number';
      const hasValidSize =
        typeof next.width === 'number' && typeof next.height === 'number';

      if (hasValidPosition && hasValidSize) {
        // 保留原始位置和大小，不重新计算
      } else {
        const childNodes = allNodes.filter(item =>
          next.children?.includes(item.id)
        );
        if (childNodes.length > 0) {
          let minX = Infinity;
          let minY = Infinity;
          let maxX = -Infinity;
          let maxY = -Infinity;
          childNodes.forEach(child => {
            if (typeof child.x === 'number' && typeof child.y === 'number') {
              const childWidth = child.width || 50;
              const childHeight = child.height || 30;
              minX = Math.min(minX, child.x - childWidth / 2);
              minY = Math.min(minY, child.y - childHeight / 2);
              maxX = Math.max(maxX, child.x + childWidth / 2);
              maxY = Math.max(maxY, child.y + childHeight / 2);
            }
          });
          if (
            isFinite(minX) &&
            isFinite(minY) &&
            isFinite(maxX) &&
            isFinite(maxY)
          ) {
            const padding = 40;
            const requiredWidth = maxX - minX + padding * 2;
            const requiredHeight = maxY - minY + padding * 2;
            next.width = Math.max(next.width || 300, requiredWidth);
            next.height = Math.max(next.height || 200, requiredHeight);
            if (!hasValidPosition) {
              next.x = (minX + maxX) / 2;
              next.y = (minY + maxY) / 2;
            }
          }
        } else {
          next.width = next.width || 300;
          next.height = next.height || 200;
        }
      }
    }

    if (next.type === 'dynamic-group') {
      ensureTextPosition(next);
      next.properties = {
        name: next.properties?.name || '',
        description: next.properties?.description || '',
        width: next.width,
        height: next.height,
        isGroup: true,
        children: next.children || next.properties?.children || [],
        nodeType: 'rect',
        autoResize: false,
        isRestrict: true,
        collapsible: false,
        isCollapsed: false,
        originalType: 'dynamic-group',
        isEntityGroup: true,
        ...next.properties,
      };
    }

    return next;
  }, []);

  const normalizeGraphData = useCallback(
    (graphData: OPMGraphData): OPMGraphData => {
      const cloned = cloneGraphData(graphData);
      let processedNodes = cloned.nodes.map((node, index, arr) =>
        processNodeDefaults(node, index, arr)
      );
      processedNodes = processedNodes.map(node =>
        processStateNodePosition(node, processedNodes)
      );
      processedNodes = processedNodes.map(node =>
        processValueNodePosition(node, processedNodes)
      );
      processedNodes = processedNodes.map(node =>
        processGroupNodeSize(node, processedNodes)
      );
      processedNodes = processedNodes.map(node =>
        processStateNodePosition(node, processedNodes)
      );
      processedNodes = processedNodes.map(node =>
        processValueNodePosition(node, processedNodes)
      );
      processedNodes = processedNodes.map((node, index) => {
        if (
          node.type === 'state' &&
          (typeof node.x !== 'number' || typeof node.y !== 'number')
        ) {
          const stateNodes = processedNodes.filter(
            item => item.type === 'state'
          );
          const stateIndex = stateNodes.findIndex(item => item.id === node.id);
          node.x = 300 + (stateIndex % 4) * 200;
          node.y = 500 + Math.floor(stateIndex / 4) * 100;
        }

        if (typeof node.x !== 'number' || typeof node.y !== 'number') {
          const gridSize = 200;
          const nodesPerRow = 3;
          const row = Math.floor(index / nodesPerRow);
          const col = index % nodesPerRow;
          node.x = 300 + col * gridSize;
          node.y = 200 + row * gridSize;
        }

        ensureTextPosition(node);

        if (node.type === 'state') {
          node.properties = {
            name: node.properties?.name || '',
            description: node.properties?.description || '',
            width: node.width,
            height: node.height,
            isState: true,
            nodeType: 'state',
            parentGroupId: node.properties?.parentGroupId || '',
            ...node.properties,
          };
        }

        if (node.type === 'value') {
          node.properties = {
            name: node.properties?.name || '',
            description: node.properties?.description || '',
            width: node.width,
            height: node.height,
            isValue: true,
            nodeType: 'value',
            parentGroupId: node.properties?.parentGroupId || '',
            ...node.properties,
          };
        }

        if (node.type === 'dynamic-group') {
          node.properties = {
            name: node.properties?.name || '',
            description: node.properties?.description || '',
            width: node.width,
            height: node.height,
            isGroup: true,
            children: node.children || node.properties?.children || [],
            nodeType: 'rect',
            autoResize: false,
            isRestrict: true,
            collapsible: false,
            isCollapsed: false,
            originalType: 'dynamic-group',
            isEntityGroup: true,
            ...node.properties,
          };
        }

        return node;
      });

      // 处理边数据：清除位置信息，让 LogicFlow 根据节点重新计算边的路径
      // 这是全量导入的关键处理，避免边的路径与节点位置不一致
      const processedEdges = (cloned.edges || []).map(edge => {
        // 只保留边的核心属性，清除位置相关的属性
        const { startPoint, endPoint, pointsList, ...rest } = edge;
        return rest;
      });

      return {
        nodes: processedNodes,
        edges: processedEdges,
      };
    },
    [
      processGroupNodeSize,
      processNodeDefaults,
      processStateNodePosition,
      processValueNodePosition,
    ]
  );

  const normalizeTree = useCallback(
    (tree: OntologyGraphTree): OntologyGraphTree =>
      tree.map(layer => ({
        ...layer,
        graphData: normalizeGraphData(layer.graphData),
        children: layer.children?.length ? normalizeTree(layer.children) : [],
      })),
    [normalizeGraphData]
  );

  const updateStateFromLayer = useCallback(
    (layer?: OntologyGraphLayer) => {
      if (!layer) return;
      const nodes = layer.graphData?.nodes || [];
      const edges = layer.graphData?.edges || [];
      const maxNodeId = extractMaxCounter(nodes);
      setNodeCounter(maxNodeId);
      setGlobalElementOrder(nodes.length + edges.length);
      const history = [
        ...nodes.map((node: any, index: number) => ({
          id: node.id,
          type: 'node' as const,
          timestamp: index + 1,
        })),
        ...edges.map((edge: any, index: number) => ({
          id: edge.id,
          type: 'edge' as const,
          timestamp: nodes.length + index + 1,
        })),
      ];
      setElementAddHistory(history);
    },
    [setElementAddHistory, setGlobalElementOrder, setNodeCounter]
  );

  const handleExportJSON = useCallback(() => {
    try {
      const tree = getGraphTree();
      const payload = createGraphPayload(tree);
      const jsonString = JSON.stringify(payload, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `opm-diagram-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('JSON导出成功');
    } catch (error) {
      console.error('JSON导出失败:', error);
      alert('JSON导出失败: ' + (error as Error).message);
    }
  }, [getGraphTree]);

  const handleImportJSON = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = e => {
        try {
          const jsonString = e.target?.result as string;
          const parsed = JSON.parse(jsonString);
          const normalized = normalizeGraphTree(parsed);
          const processedTree = normalizeTree(normalized);
          onTreeImport(processedTree);
          updateStateFromLayer(processedTree[0]);

          setTimeout(() => {
            handleResetView();
          }, 300);

          updateGraphData();
          setTimeout(() => {
            updateGraphData();
          }, 100);
        } catch (error) {
          console.error('JSON导入失败:', error);
          alert('JSON导入失败: ' + (error as Error).message);
        }
      };

      reader.onerror = () => {
        alert('文件读取失败');
      };
      reader.readAsText(file);
      event.target.value = '';
    },
    [
      handleResetView,
      normalizeTree,
      onTreeImport,
      updateGraphData,
      updateStateFromLayer,
    ]
  );

  return {
    handleExportJSON,
    handleImportJSON,
  };
};
